import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import type { SummaryFieldBucket } from "@/modules/forms/domain/lib/build-form-responses-summary";
import type { FormResponsesSummaryDTO } from "@/modules/forms/presentation/api/types";
import { BarChart } from "@/shared/ui/BarChart";

type FormResponsesChartsProps = {
  summary?: FormResponsesSummaryDTO | null;
};

function RatingChart({
  field,
}: {
  field: Extract<SummaryFieldBucket, { kind: "rating" }>;
}) {
  const entries = [...Object.entries(field.distribution)].sort(
    ([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10),
  );
  const data = entries.map(([k, count]) => ({
    label: String(k),
    count: count as number,
  }));

  const config = {
    count: { label: "ຈຳນວນ", color: "var(--chart-1)" },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{field.label}</CardTitle>
        <CardDescription>
          ຈຳນວນຄຳຕອບ: {field.count}
          {typeof field.average === "number"
            ? ` · ສະເລ່ຍ ${field.average.toFixed(2)}`
            : ""}{" "}
          · ມາດຕະຖານ 1–{field.maxScale}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[220px] w-full">
          <BarChart
            config={config}
            data={data}
            xDataKey="label"
            valueDataKey="count"
            className="h-full"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ChoiceChart({
  field,
}: {
  field: Extract<SummaryFieldBucket, { kind: "singleChoice" | "multiChoice" }>;
}) {
  const data = field.frequencies.map((f) => ({
    label: f.label ?? f.value,
    count: f.count,
  }));
  const config = {
    count: { label: "ຈຳນວນ", color: "var(--chart-1)" },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{field.label}</CardTitle>
        <CardDescription>
          {field.kind === "multiChoice" ? "ເລືອກຫຼາຍທາງ" : "ເລືອກຫນຶ່ງ"}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[240px] w-full">
          <BarChart
            config={config}
            data={data}
            xDataKey="label"
            valueDataKey="count"
            className="h-full"
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function FormResponsesCharts({ summary }: FormResponsesChartsProps) {
  if (!summary) {
    return null;
  }

  if (summary.totalSubmissions === 0) {
    return (
      <Card>
        <CardContent className="text-center text-muted-foreground text-sm">
          ຍັງບໍ່ມີຄຳຕອບ — ສະຫຼຸບຈະສະແດງເມື່ອມີຂໍ້ມູນ
        </CardContent>
      </Card>
    );
  }

  const fields = summary.fields;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">ຈຳນວນຜົນຕອບທັງໝົດ</span>
          <span className="font-semibold text-foreground text-lg tabular-nums">
            {summary.totalSubmissions}
          </span>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {fields.map((f) => {
          if (f.kind === "rating") {
            return <RatingChart key={f.fieldId} field={f} />;
          }
          if (f.kind === "singleChoice" || f.kind === "multiChoice") {
            return <ChoiceChart key={f.fieldId} field={f} />;
          }
          if (f.kind === "scalar") {
            return (
              <Card key={f.fieldId}>
                <CardHeader>
                  <CardTitle className="text-base">{f.label}</CardTitle>
                  <CardDescription>ຂໍ້ຄວາມ, ຕົວເລກ, ວັນທີ, ໄຟລ໌</CardDescription>
                </CardHeader>
                <CardContent className="space-y-1 text-muted-foreground text-sm leading-relaxed">
                  <p>
                    ແຖວທີ່ມີຄ່າ: {f.nonEmptyCount} / {f.rowCount}
                  </p>
                </CardContent>
              </Card>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
